/**
 * Is this instance the one the user can actually see?
 *
 * `MindspaceLayout` renders its children TWICE — once in a `hidden md:flex`
 * desktop subtree and once in a `md:hidden` mobile one — and lets CSS pick.
 * Both copies are mounted at every viewport, which is fine for markup and
 * catastrophic for `window`-level keyboard shortcuts: each copy binds its own
 * listener, so ONE key press runs the handler twice.
 *
 * Measured: a single `3` on a flashcard submitted two FSRS reviews with two
 * different idempotency tokens, so the backend's `(user, concept, attempt_id)`
 * dedupe could not collapse them either — the grade was applied twice, every
 * time, for every keyboard user. A mouse click was always correct, because a
 * click can only land on the copy that is on screen.
 *
 * Every window-level shortcut in the runner gates on this.
 */
export function isOnScreen(el: HTMLElement | null | undefined): boolean {
  if (!el) return false;
  // `checkVisibility` accounts for `display:none` on any ancestor, which is
  // exactly how the layout hides the off-breakpoint copy.
  if (typeof el.checkVisibility === 'function') return el.checkVisibility();
  return !!(el.offsetParent || el.getClientRects().length);
}

/**
 * Is the user typing? Then a bare letter is a character, not a shortcut.
 *
 * Every window-level shortcut in the runner shields on this — `s`, `u`, the
 * digits and Space all have to yield inside an input, or the custom-session
 * search field would star concepts as you typed the word "stars". Shared
 * because it was four identical copies, one per shortcut.
 */
export function isTypingTarget(e: KeyboardEvent): boolean {
  const target = e.target as HTMLElement | null;
  return (
    !!target &&
    (target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.isContentEditable)
  );
}
